/**
 * H.2 — czyste pomocniki formularza zgłoszeń z przycisku „?” (bez Reacta i bez fetch).
 *
 * Progi są LUSTREM `Login/backend/routes/support.js` (H.1): formularz ma odmówić wcześniej
 * i czytelniej niż serwer, ale o tym, co przejdzie, i tak rozstrzyga serwer.
 */

export const KATEGORIE = ['bug', 'question', 'idea', 'other'];
export const MAKS_TEMAT = 150;
export const MIN_TRESC = 10;
export const MAKS_TRESC = 5000;
export const MAKS_STRONA = 200;
export const MAKS_ZRZUTOW = 3;

// Zapasowa droga, gdy serwer nie wyśle zgłoszenia (502, 503) albo nie ma sieci. Ten sam adres, który
// serwer ma jako domyślny; przejście na osobny alias ustawia się zmienną SUPPORT_EMAIL po stronie
// serwera, a ten adres i tak zostaje aktywny.
export const ADRES_ZGLOSZEN = 'kontakt@the-rent.pl';

// Ten sam kształt co `PROSTY_EMAIL` w routes/support.js: bez odstępów, przecinków, średników
// i nawiasów, więc do `mailto:` nie da się dokleić drugiego adresu ani parametru (`?cc=`).
const PROSTY_EMAIL = /^[^\s@<>()",;:\\[\]?&=#%]+@[^\s@<>()",;:\\[\]?&=#%]+\.[^\s@<>()",;:\\[\]?&=#%]+$/;

/**
 * Adres dla zapasowego `mailto:`. Serwer przy 502, 429 i 503 oddaje skonfigurowany adres zgłoszeń
 * (`SUPPORT_EMAIL`), żeby zgłoszenie wysłane mailem trafiło tam, gdzie te z panelu. Bez odpowiedzi
 * serwera (brak sieci) albo z czymkolwiek, co nie wygląda na jeden adres — stała domyślna.
 */
export function adresZapasowy(zSerwera) {
  const s = typeof zSerwera === 'string' ? zSerwera.trim() : '';
  return s && s.length <= 254 && PROSTY_EMAIL.test(s) ? s : ADRES_ZGLOSZEN;
}

// Przedrostek i nazwy kategorii w temacie maila są DANYMI, nie tekstem interfejsu: reguła poczty
// na skrzynce zgłoszeń rozpoznaje zgłoszenia po przedrostku, a serwer (routes/support.js) używa
// dokładnie tych samych słów. Zgłoszenie wysłane zapasowym `mailto:` ma trafić do tego samego
// folderu, więc tych napisów nie wolno tłumaczyć na język panelu.
export const PRZEDROSTEK_TEMATU = '[the.rENT][Zgłoszenie]';
export const ETYKIETY_KATEGORII = Object.freeze({ bug: 'Błąd', question: 'Pytanie', idea: 'Propozycja', other: 'Inne' });

/** Przycięcie do `limit` znaków bez rozcinania pary surogatów (emotki). */
function przytnij(tekst, limit) {
  const znaki = Array.from(String(tekst ?? ''));
  return znaki.length > limit ? znaki.slice(0, limit).join('') : znaki.join('');
}

/**
 * Sama ścieżka strony: bez zapytania i fragmentu (bywają w nich identyfikatory i tokeny),
 * przycięta jak na serwerze. To samo pokazujemy w „Co jeszcze wyślemy” i to samo wysyłamy.
 */
export function sciezkaStrony(pathname) {
  const sciezka = String(pathname ?? '').split(/[?#]/)[0].trim();
  if (!sciezka) return '/';
  return przytnij(sciezka.startsWith('/') ? sciezka : `/${sciezka}`, MAKS_STRONA);
}

/** Rozmiar okna w formacie, który przyjmuje serwer (`1280x720`). */
export function rozmiarOkna(win) {
  const w = Math.round(Number(win && win.innerWidth) || 0);
  const h = Math.round(Number(win && win.innerHeight) || 0);
  return w > 0 && h > 0 ? `${w}x${h}` : '';
}

/**
 * Dokłada zrzuty do listy: tylko obrazy i najwyżej `max` łącznie. Zwraca nową listę
 * i to, ile pominięto (i dlaczego), żeby okno mogło to powiedzieć zamiast milczeć.
 */
export function dodajZrzuty(obecne, nowe, max = MAKS_ZRZUTOW) {
  const lista = Array.isArray(obecne) ? [...obecne] : [];
  let nieObrazy = 0;
  let ponadLimit = 0;
  for (const plik of Array.from(nowe || [])) {
    if (!plik || !/^image\//.test(plik.type || '')) { nieObrazy += 1; continue; }
    if (lista.length >= max) { ponadLimit += 1; continue; }
    lista.push(plik);
  }
  return { lista, nieObrazy, ponadLimit };
}

/** Lokalna walidacja przed wysłaniem. Kody te same co w odpowiedziach serwera. */
export function sprawdzFormularz({ kategoria, temat, tresc }) {
  if (!KATEGORIE.includes(kategoria)) return 'invalid_category';
  const t = String(temat ?? '').trim();
  if (!t) return 'subject_required';
  if (t.length > MAKS_TEMAT) return 'subject_too_long';
  const m = String(tresc ?? '').trim();
  if (m.length < MIN_TRESC) return 'message_too_short';
  if (m.length > MAKS_TRESC) return 'message_too_long';
  return null;
}

/**
 * Link `mailto:` z gotowym tematem i treścią, gdy wysyłka przez serwer się nie udała.
 *
 * Długi `mailto:` bywa ucinany przez klienta poczty (Outlook w okolicach 2 000 znaków), a ucięty
 * w środku kodowania `%XX` potrafi się w ogóle nie otworzyć. Dlatego przycinamy TREŚĆ opisu
 * (kontekst i dopisek o zrzutach zostają całe) tak, żeby cały adres zmieścił się w `maksDlugosc`.
 *
 * `kontekst` to pary [etykieta, wartość] w języku panelu; `dopisek` to zdanie o zrzutach
 * (mailto nie przenosi załączników), przekazywane tylko wtedy, gdy użytkownik jakieś dodał.
 */
export function buildMailto({
  to = ADRES_ZGLOSZEN,
  kategoria,
  temat,
  tresc,
  kontekst = [],
  dopisek = '',
  maksDlugosc = 1800,
}) {
  const etykieta = ETYKIETY_KATEGORII[kategoria] || ETYKIETY_KATEGORII.other;
  const subject = `${PRZEDROSTEK_TEMATU}[${etykieta}] ${String(temat ?? '').trim()}`.trim();
  const stopka = [
    '',
    '---',
    ...kontekst.filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`),
    ...(dopisek ? ['', dopisek] : []),
  ].join('\n');

  const poczatek = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=`;
  const opis = String(tresc ?? '').trim();
  const zbuduj = (o) => `${poczatek}${encodeURIComponent(`${o}\n${stopka}`)}`;

  if (zbuduj(opis).length <= maksDlugosc) return zbuduj(opis);
  // Najdłuższy początek opisu, który się mieści (wyszukiwanie połówkowe po znakach).
  const znaki = Array.from(opis);
  let lo = 0;
  let hi = znaki.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (zbuduj(`${znaki.slice(0, mid).join('')}…`).length <= maksDlugosc) lo = mid; else hi = mid - 1;
  }
  return zbuduj(`${znaki.slice(0, lo).join('')}…`);
}

/* ── Szkic w sessionStorage ─────────────────────────────────────────────────────────────────
   Zamknięcie okna nie kasuje wpisanego tekstu (znika dopiero po wysłaniu albo z końcem karty).
   Obrazów nie trzymamy: są za duże na sessionStorage. Każdy dostęp w try/catch, bo tryb
   prywatny i zablokowane dane witryny potrafią rzucać już przy samym odczycie. */

export const kluczSzkicu = (userId) => `supportDraft:v1:${userId ?? 'anon'}`;

export function wczytajSzkic(storage, userId) {
  try {
    const surowy = storage && storage.getItem(kluczSzkicu(userId));
    if (!surowy) return null;
    const szkic = JSON.parse(surowy);
    if (!szkic || typeof szkic !== 'object') return null;
    return {
      kategoria: KATEGORIE.includes(szkic.kategoria) ? szkic.kategoria : null,
      temat: typeof szkic.temat === 'string' ? szkic.temat.slice(0, MAKS_TEMAT * 2) : '',
      tresc: typeof szkic.tresc === 'string' ? szkic.tresc.slice(0, MAKS_TRESC * 2) : '',
    };
  } catch (_) {
    return null;
  }
}

export function zapiszSzkic(storage, userId, { kategoria, temat, tresc }) {
  try {
    if (!storage) return;
    if (!String(temat ?? '').trim() && !String(tresc ?? '').trim()) {
      storage.removeItem(kluczSzkicu(userId));
      return;
    }
    storage.setItem(kluczSzkicu(userId), JSON.stringify({ kategoria, temat, tresc }));
  } catch (_) { /* brak miejsca albo zablokowane dane witryny: szkic po prostu nie przetrwa */ }
}

export function usunSzkic(storage, userId) {
  try { if (storage) storage.removeItem(kluczSzkicu(userId)); } catch (_) { /* jak wyżej */ }
}
